import React, { useEffect, useState } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { Container, Link } from '@mui/material';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { _axios as axios, cancelToken, customFetch } from '../../utils/auth';

import { useAppSelector, useAppDispatch } from '../../store';
import { addLocations, selectCategory, selectLocation } from '../../store/mobileSlice';
import { url } from '../../utils';

import useDebounceValue from '../../hooks/useDebounceValue';

import SelectListLocation from './SelectListLocation';
import SearchLocation from './SearchLocation';

const pageLocationName = {
  th: {
    title: 'ที่ตั้ง',
    inputPlaceholder: 'พิมพ์ชื่อสถานที่',
  },
  en: {
    title: 'Location',
    inputPlaceholder: 'Input location keyword',
  },
  cn: {
    title: '地点',
    inputPlaceholder: '输入区域名',
  },
};

interface ListTypeMobileProps {
  open: boolean;
  listType: 'action' | 'category' | 'locations' | 'price';
  handlePopupSelected: (key: string, value: boolean) => void;
  language: 'th' | 'en' | 'cn';
}

export interface LocationsListType {
  value: string;
  name: string;
}

const SelectLocationsOnMobile: React.FC<ListTypeMobileProps> = ({ open, listType, handlePopupSelected, language }) => {
  const dispatch = useAppDispatch();
  const locations = useAppSelector(selectLocation);
  const category = useAppSelector(selectCategory);
  const [isCancelSearchPopup, setIsCancelSearchPopup] = useState<boolean>(true);
  const [locationsList, setLocationList] = useState<LocationsListType[] | []>([]);
  const [selectLoctionList, setSelectLocationList] = useState<LocationsListType[]>([{ value: 'All', name: 'All' }]);
  const [isStepper, setIsStepper] = useState({
    locationType: false,
    locations: false,
    locationBranch: false,
  });
  const [locationTypeId, setLocationTypeId] = useState<string>('');
  const [keyword, setKeyword] = useState<string>('');
  const [searchLoctionList, setSearchLocationList] = useState<string[]>([]);
  const finalKeyword = useDebounceValue(keyword);

  useEffect(() => {
    if (finalKeyword) {
      axios
        .post(
          `${url}/v1/search/name/mobile`,
          {
            keyword: keyword,
          },
          {
            headers: {
              'Accept-Language': `${language}`,
            },
          },
        )
        .then((response) => {
          setSearchLocationList(response.data.data);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [finalKeyword]);

  useEffect(() => {
    getLocationType();
  }, []);

  const getLocationType = () => {
    axios.get(`${url}/v1/location_type?language=${language}`).then((response) => {
      setLocationList(response.data.data);
    });
  };

  const getLocations = async (location_type_id: string): Promise<LocationsListType[]> => {
    const response = await axios.get(
      `${url}/v1/locations/type/${location_type_id}?language=${language}&category=${category}`,
    );

    return response.data.data;
  };

  const getLocationBranch = async (location_type_id: string, locations_id: string): Promise<LocationsListType[]> => {
    const response = await axios.get(
      `${url}/v1/location_branch/type/${location_type_id}/locations/${locations_id}?language=${language}`,
    );

    return response.data.data;
  };

  const handleResetLocationType = () => {
    setLocationTypeId('');
    getLocationType();
    setIsStepper({
      locationType: false,
      locations: false,
      locationBranch: false,
    });
  };

  const handleResetLocationsList = async (location_type_id: string) => {
    const locations = await getLocations(location_type_id);
    setLocationList(locations);
    setIsStepper({
      locationType: true,
      locations: false,
      locationBranch: false,
    });
  };

  const handleSelectLocation = async (event: LocationsListType) => {
    if (!isStepper.locationType) {
      if (event.value === '0') {
        handlePopupSelected('locations', false);
      } else {
        dispatch(addLocations(event.name));
        setSelectLocationList((prevState) => [...prevState, event]);
        setLocationTypeId(event.value);
        setIsStepper((prevState) => ({ ...prevState, locationType: true }));
        const locations = await getLocations(event.value);
        setLocationList(locations);
      }
    } else if (!isStepper.locations) {
      if (event.value === '0') {
        handlePopupSelected('locations', false);
      } else {
        const results = `${locations} > ${event.name}`;
        dispatch(addLocations(results));
        setSelectLocationList((prevState) => [...prevState, event]);
        if (locationTypeId === '26') {
          handlePopupSelected('locations', false);

          return;
        }
        const location_branch = await getLocationBranch(locationTypeId, event.value);
        if (location_branch.length === 1) {
          handlePopupSelected('locations', false);
        } else {
          setLocationList(location_branch);
          setIsStepper((prevState) => ({ ...prevState, locations: true }));
        }
      }
    } else if (!isStepper.locationBranch) {
      if (event.value === '0') {
        handlePopupSelected('locations', false);
      } else {
        const results = `${locations} > ${event.name}`;
        dispatch(addLocations(results));
        setSelectLocationList((prevState) => [...prevState, event]);
        handlePopupSelected('locations', false);
      }
    }
  };

  return (
    <Box
      className={`${
        open ? 'opacity-100 z-20' : 'opacity-0 -z-10'
      } absolute bg-[#f3f3f3] w-screen h-screen top-0 right-0 py-6`}>
      <Container>
        <IconButton
          className={`!absolute top-5 ${!isCancelSearchPopup && '!hidden'}`}
          onClick={() => handlePopupSelected(listType, false)}>
          <ArrowBackIosNewIcon />
        </IconButton>
        <Typography sx={{ mb: 2 }} variant="h6" className={`text-center ${!isCancelSearchPopup && '!hidden'}`}>
          {pageLocationName[language].title}
        </Typography>
        <Box className="flex justify-center items-center mb-4 gap-x-2">
          <div className="flex items-center w-full bg-white rounded gap-x-4 px-3 py-1.5">
            <SearchIcon />
            <input
              type="text"
              className="w-full border-none focus:outline-none text-lg h-10"
              placeholder={pageLocationName[language].inputPlaceholder}
              onChange={(e) => {
                setKeyword(e.target.value);
                setIsCancelSearchPopup(false);
              }}
              value={keyword}
            />
            {keyword && (
              <IconButton onClick={() => setKeyword('')}>
                <CloseIcon />
              </IconButton>
            )}
          </div>
          {!isCancelSearchPopup && (
            <Button
              color="inherit"
              onClick={() => {
                setKeyword('');
                setIsCancelSearchPopup(true);
              }}>
              cancel
            </Button>
          )}
        </Box>
        <Breadcrumbs className={`${!isCancelSearchPopup && '!hidden'}`} sx={{ mb: 2 }} aria-label="breadcrumb">
          {selectLoctionList.map((list, index) => (
            <Link
              key={list.value}
              color="text.primary"
              underline="hover"
              sx={{
                cursor: 'pointer',
              }}
              onClick={async () => {
                const results = selectLoctionList.slice(0, index + 1);

                const resultsShowTemp = selectLoctionList.slice(0, index + 1);
                resultsShowTemp.shift();
                if (resultsShowTemp.length > 1) {
                  dispatch(addLocations(resultsShowTemp.map((item) => item.name).join(' > ')));
                } else if (resultsShowTemp.length === 1) {
                  dispatch(addLocations(resultsShowTemp[0].name));
                }

                setSelectLocationList(results);
                // ย้อนหน้ารายการกลับไปที่เลือก
                if (results.length === 1) {
                  handleResetLocationType();
                } else if (results.length === 2) {
                  handleResetLocationsList(results[1].value);
                }
              }}>
              {list.name}
            </Link>
          ))}
        </Breadcrumbs>
        {isCancelSearchPopup && !keyword && (
          <Box
            className={`w-full bg-white overflow-y-auto `} //${isCancelSearchPopup && !keyword && '!hidden'}
            style={{
              height: 'calc(100vh - (32px + 52px + 28px + 16px*3 + 24px*2))',
            }}>
            <SelectListLocation data={locationsList} handleSelectLocation={handleSelectLocation} />
          </Box>
        )}
        {!isCancelSearchPopup && (
          <Box
            className={`w-full bg-white overflow-y-auto`}
            style={{
              height: 'calc(100vh - (52px + 16px + 24px*2))',
            }}>
            <SearchLocation data={searchLoctionList} handlePopupSelected={handlePopupSelected} />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default SelectLocationsOnMobile;
